import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TextInput, TouchableOpacity, ActivityIndicator, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';
import { createReferralRequest, updateReferralRequest, getMyReferralRequests } from '../../lib/api/referrals';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

type CreateReferralNavigationProp = StackNavigationProp<ClubsStackParamList, 'CreateReferral'>;
type CreateReferralRouteProp = RouteProp<ClubsStackParamList, 'CreateReferral'>;

const countWords = (text: string) =>
  text.trim() === '' ? 0 : text.trim().split(/\s+/).length;

const CreateReferralScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<CreateReferralNavigationProp>();
  const route = useRoute<CreateReferralRouteProp>();
  const { clubId, requestId } = route.params;
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [businessName, setBusinessName] = useState('');
  const [businessDetails, setBusinessDetails] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  const isEditMode = !!requestId;

  useEffect(() => {
    if (isEditMode && requestId) {
      loadReferralData();
    }
  }, [requestId]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      const requests = await getMyReferralRequests(clubId);
      const request = requests.find(r => r.id === requestId);
      if (request) {
        setBusinessName(request.businessName);
        setBusinessDetails(request.businessDetails);
        setLookingFor(request.lookingFor);
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to load referral request');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!businessName.trim()) {
      showErrorToast('Please enter business name');
      return;
    }
    if (!businessDetails.trim()) {
      showErrorToast('Please enter business details');
      return;
    }
    if (countWords(businessDetails) > 140) {
      showErrorToast('About Your Business must be 140 words or less');
      return;
    }
    if (!lookingFor.trim()) {
      showErrorToast('Please enter what you are looking for');
      return;
    }
    if (countWords(lookingFor) > 30) {
      showErrorToast('Looking For must be 30 words or less');
      return;
    }

    try {
      setSubmitting(true);
      if (isEditMode && requestId) {
        await updateReferralRequest(requestId, {
          businessName: businessName.trim(),
          businessDetails: businessDetails.trim(),
          lookingFor: lookingFor.trim(),
        });
        showSuccessToast('Referral request updated successfully!');
      } else {
        await createReferralRequest({
          clubId,
          businessName: businessName.trim(),
          businessDetails: businessDetails.trim(),
          lookingFor: lookingFor.trim(),
        });
        showSuccessToast('Referral request posted successfully!');
      }
      navigation.goBack();
    } catch (error: any) {
      // Use Alert for longer error messages to ensure full text is visible
      if (error.message?.includes('You can only post one referral per week')) {
        Alert.alert('Referral Limit Reached', error.message);
      } else {
        showErrorToast(error.message || `Failed to ${isEditMode ? 'update' : 'post'} referral request`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = businessName.trim() && businessDetails.trim() && lookingFor.trim() && countWords(businessDetails) <= 140 && countWords(lookingFor) <= 30;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {/* Header */}
      <View 
        style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {isEditMode ? 'Edit Referral Request' : 'Post Referral Request'}
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.accent} />
            <ThemedText style={[styles.loadingText, { color: theme.textSecondary }]}>
              Loading...
            </ThemedText>
          </View>
        ) : (
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {/* Info Banner */}
          <View style={[styles.infoBanner, { backgroundColor: theme.accent + '15', borderColor: theme.accent + '30' }]}>
            <Ionicons name="information-circle" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoText, { color: theme.accent }]}>
              Post your referral request and let club members help you find the right connections!
            </ThemedText>
          </View>

          {/* Business Name */}
          <View style={styles.formGroup}>
            <View style={styles.labelRow}>
              <Ionicons name="business-outline" size={18} color={theme.accent} />
              <ThemedText style={[styles.label, { color: theme.text }]}>
                Business Name *
              </ThemedText>
            </View>
            <TextInput
              style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
              placeholder="Enter business or company name"
              placeholderTextColor={theme.textTertiary}
              value={businessName}
              onChangeText={setBusinessName}
              maxLength={20}
            />
            <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
              The name of the business you're representing
            </ThemedText>
          </View>

          {/* Business Details */}
          <View style={styles.formGroup}>
            <View style={styles.labelRow}>
              <Ionicons name="information-circle-outline" size={18} color={theme.accent} />
              <ThemedText style={[styles.label, { color: theme.text }]}>
                About Your Business *
              </ThemedText>
            </View>
            <TextInput
              style={[
                styles.input,
                styles.textArea,
                { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }
              ]}
              placeholder="Describe your business, what you do, your services or products..."
              placeholderTextColor={theme.textTertiary}
              value={businessDetails}
              onChangeText={setBusinessDetails}
              multiline
              numberOfLines={5}
              textAlignVertical="top"
            />
            <ThemedText style={[styles.hint, { color: countWords(businessDetails) > 140 ? theme.error || '#D32F2F' : theme.textTertiary }]}>
              {countWords(businessDetails)}/140 words
            </ThemedText>
          </View>

          {/* Looking For */}
          <View style={styles.formGroup}>
            <View style={styles.labelRow}>
              <Ionicons name="search-outline" size={18} color={theme.accent} />
              <ThemedText style={[styles.label, { color: theme.text }]}>
                Looking For *
              </ThemedText>
            </View>
            <TextInput
              style={[
                styles.input,
                styles.textArea,
                { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }
              ]}
              placeholder="What kind of connection or introduction are you seeking? Be specific..."
              placeholderTextColor={theme.textTertiary}
              value={lookingFor}
              onChangeText={setLookingFor}
              multiline
              numberOfLines={5}
              textAlignVertical="top"
            />
            <ThemedText style={[styles.hint, { color: countWords(lookingFor) > 30 ? theme.error || '#D32F2F' : theme.textTertiary }]}>
              {countWords(lookingFor)}/30 words
            </ThemedText>
          </View>

          {/* Example */}
          <View style={[styles.exampleBox, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <ThemedText style={[styles.exampleTitle, { color: theme.text }]}>
              Example:
            </ThemedText>
            <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
              <ThemedText style={{ fontWeight: '600' }}>Business:</ThemedText> XYZ Marketing Agency
            </ThemedText>
            <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
              <ThemedText style={{ fontWeight: '600' }}>About:</ThemedText> We're a digital marketing agency specializing in social media management and content creation for small businesses.
            </ThemedText>
            <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
              <ThemedText style={{ fontWeight: '600' }}>Looking For:</ThemedText> Introduction to business owners in the retail or hospitality sector who need help growing their online presence.
            </ThemedText>
          </View>

          {/* Submit Button */}
          <TouchableOpacity
            style={[
              styles.submitButton,
              { backgroundColor: isFormValid ? theme.accent : theme.border },
            ]}
            onPress={handleSubmit}
            disabled={!isFormValid || submitting || loading}
            activeOpacity={0.7}
          >
            {submitting ? (
              <ActivityIndicator color={theme.textInverse} />
            ) : (
              <>
                <Ionicons name="send" size={20} color={isFormValid ? theme.textInverse : theme.textTertiary} />
                <ThemedText
                  style={[
                    styles.submitButtonText,
                    { color: isFormValid ? theme.textInverse : theme.textTertiary }
                  ]}
                >
                  {isEditMode ? 'Update Request' : 'Post Request'}
                </ThemedText>
              </>
            )}
          </TouchableOpacity>

          <FormFooterSpacer
            headerHeight={headerHeight}
            bottomInset={bottomInset}
            keyboardHeight={keyboardHeight}
          />
        </ScrollView>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  flex: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 14,
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 24,
    gap: 10,
    borderWidth: 1,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  formGroup: {
    marginBottom: 24,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  hint: {
    fontSize: 12,
    marginTop: 6,
  },
  exampleBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 16,
    marginTop: 8,
  },
  exampleTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  exampleText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 6,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 10,
    gap: 8,
    marginTop: 24,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CreateReferralScreen;
